import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Popover, Select, TextInput, Tooltip } from '@mantine/core';
import { TbCheck, TbColumnInsertRight, TbDownload, TbPlus, TbSearch } from 'react-icons/tb';
import CanvasTable, {
  CanvasTableEditEvent,
  CanvasTableActionEvent,
  CanvasTableSchemaColumn
} from '../../../../components/CanvasTable';
import { IMAGE_TYPES } from '../../../../components/CanvasTable/constants';
import { DeleteConfirmModal } from '../../../../components/DeleteConfirmModal';
import { AddRowFooter, Main, MainActions, MainHeader, ToolBtn } from './style';
import { PermissionGuard } from '../../../../components/PermissionGuard';
import type { LookupColumn, LookupColumnType, LookupDictionary, LookupRow } from './types';
import { idCreator } from '../../../Project/utils/idCreator';

interface Props {
  idPrefix?: string;
  dictionary: LookupDictionary;
  onChange: (next: LookupDictionary) => void;
  onSave?: () => void;
  dirty?: boolean;
  isSaving?: boolean;
  canManageWorkflow?: boolean;
}

const generateRowId = (): string => `row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const isMac =
  typeof navigator !== 'undefined' &&
  /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent || '');
const SAVE_KEY_LABEL = isMac ? '⌘' : 'Ctrl';

// Synthetic columns we own end-to-end. `__` prefix guarantees no collision
// with a user-defined column key.
//   * `ROWNUM_COL` — replaces canvas-datagrid's built-in row-header column
//     (which doesn't honour style keys or fire `rendercell` for cells in
//     this build), so we render the leftmost auto-numbered column ourselves.
//   * `DELETE_COL` — trailing trash-icon affordance.
const ROWNUM_COL = '__rownum';
const DELETE_COL = '__delete';
// Painted via `onRenderCell` below, not the schema's `formatter` — we draw a
// proper trash icon as canvas paths so it stays sharp at any DPR and matches
// the muted-gray treatment of the old `RowDeleteButton`.

// Tiny trash icon rendered as canvas paths. ~11x12 outlined glyph centred on
// (cx, cy); colour comes from the current `ctx.strokeStyle`.
const drawTrashIcon = (ctx: CanvasRenderingContext2D, cx: number, cy: number): void => {
  const w = 10;
  const h = 11;
  const left = cx - w / 2;
  const top = cy - h / 2;

  ctx.lineWidth = 1.25;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  ctx.beginPath();
  // Small handle on top.
  ctx.moveTo(left + 3, top);
  ctx.lineTo(left + w - 3, top);
  // Lid (horizontal line just below the handle).
  ctx.moveTo(left - 0.5, top + 1.75);
  ctx.lineTo(left + w + 0.5, top + 1.75);
  // Bin body — sides + bottom.
  ctx.moveTo(left + 1.5, top + 2.5);
  ctx.lineTo(left + 2, top + h - 0.25);
  ctx.lineTo(left + w - 2, top + h - 0.25);
  ctx.lineTo(left + w - 1.5, top + 2.5);
  // Two inner tick marks.
  ctx.moveTo(left + w / 2 - 1.5, top + 4);
  ctx.lineTo(left + w / 2 - 1.5, top + h - 2);
  ctx.moveTo(left + w / 2 + 1.5, top + 4);
  ctx.lineTo(left + w / 2 + 1.5, top + h - 2);
  ctx.stroke();
};

// Type-icon cache reusing the PNG assets the transformation table uses
// (`typography.png` / `numbers.png` / `calendar.png` via `IMAGE_TYPES`).
// Lazy-initialised at module scope so re-renders reuse already-loaded
// `Image` instances and we don't re-fetch on every paint pass.
const LOOKUP_TYPE_ICONS: Partial<Record<LookupColumnType, HTMLImageElement>> = {};

const getLookupTypeIcon = (type: LookupColumnType): HTMLImageElement | null => {
  if (!LOOKUP_TYPE_ICONS[type]) {
    const factory =
      type === 'number'
        ? IMAGE_TYPES?.float
        : type === 'datetime'
          ? IMAGE_TYPES?.datetime
          : IMAGE_TYPES?.string;
    if (typeof factory === 'function') {
      LOOKUP_TYPE_ICONS[type] = factory();
    }
  }
  return LOOKUP_TYPE_ICONS[type] ?? null;
};

const escapeCsv = (v: string | undefined): string => {
  if (v == null) return '';
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const buildCsv = (dict: LookupDictionary): string => {
  const header = dict.columns.map(c => escapeCsv(c.label)).join(',');
  const body = dict.rows
    .map(r => dict.columns.map(c => escapeCsv(r.values[c.key])).join(','))
    .join('\n');
  return `${header}\n${body}`;
};

const downloadCsv = (filename: string, content: string): void => {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// Theme overrides applied to canvas-datagrid via `gridStyleOverrides`.
// Tracks the table styles in the reference LookupsWorkspace.module.css —
// each line below ties a canvas-datagrid style key back to a CSS class.
// Mantine CSS vars resolved to their default-theme hex values so they
// survive outside the Mantine runtime that the reference uses.
//
// Note on fonts: canvas-datagrid hands `cellFont` straight to `ctx.font`,
// which silently rejects any shorthand that includes a font-weight prefix
// when one of the families is also quoted. Tried `600 12px 'Gilroy', …` →
// every cell painted with that style rendered as a blank rectangle. We
// therefore leave the existing CanvasTable `updateStyles` font path
// (`'<size>px 'Gilroy', …'`) alone and only override colours / padding /
// alignment from here.
const GRID_THEME = {
  // .colHeader — gray-1 bg, 2px gray-3 bottom border, gray-2 right.
  // Font is set via the style key (not ctx mutation in `handleRenderText`)
  // so the bold/dark state never leaks into canvas-datagrid's next text
  // paint — the row-header digit's. Important: keep the row-header
  // independent of column-header rendering.
  columnHeaderCellBackgroundColor: '#f1f3f5',
  columnHeaderCellColor: '#212529',
  columnHeaderCellFont: 'bold 14px sans-serif',
  columnHeaderCellFontSize: 14,
  columnHeaderCellFontWeight: 'bold',
  columnHeaderCellHorizontalAlignment: 'left',
  columnHeaderCellVerticalAlignment: 'center',
  columnHeaderCellPaddingLeft: 12,
  columnHeaderCellPaddingRight: 12,
  columnHeaderCellHeight: 36,
  // canvas-datagrid's built-in row-header column — we paint the digits
  // ourselves via `onAfterRenderCell`. CanvasTable auto-hides the default
  // digit text when that prop is set (mirrors text colour to bg), so we
  // only need to supply backgrounds + alignment here.
  rowHeaderCellBackgroundColor: '#fafbfc',
  rowHeaderCellHoverBackgroundColor: '#fafbfc',
  rowHeaderCellSelectedBackgroundColor: '#fafbfc',
  rowHeaderCellHorizontalAlignment: 'center',
  rowHeaderCellVerticalAlignment: 'center',
  rowHeaderCellPaddingLeft: 0,
  rowHeaderCellPaddingRight: 0,
  // .cornerCell — gray-1 bg matching the column-header row. canvas-datagrid
  // otherwise inherits the row-header background here.
  cornerCellBackgroundColor: '#f1f3f5',
  cornerCellColor: '#adb5bd',
  // .dataCell — dark-7 text, white bg, padding 6 12, 13px nowrap.
  cellColor: '#212529',
  cellBackgroundColor: '#ffffff',
  cellHorizontalAlignment: 'left',
  cellVerticalAlignment: 'center',
  cellPaddingLeft: 12,
  cellPaddingRight: 12,
  cellPaddingTop: 6,
  cellPaddingBottom: 6,
  cellHeight: 32,
  // 1px gray-1 hairlines matching `.dataCell { border: 1px solid gray-1 }`.
  cellGridLineColor: '#f1f3f5',
  cellGridLineTapeColor: '#f1f3f5',
  // .dataRow:hover — gray-0.
  cellHoverBackgroundColor: '#f8f9fa',
  // .inlineInput :focus — inset 2px blue-5 ring.
  activeCellBackgroundColor: '#ffffff',
  activeCellBorderColor: '#339af0',
  activeCellOverlayBorderColor: '#339af0',
  activeCellOverlayBorderWidth: 2,
  activeCellColor: '#212529',
  // Selection — Mantine blue-0 to align with sidebar `.lookupItemActive`.
  cellSelectedBackgroundColor: '#e7f5ff',
  cellSelectedColor: '#1c7ed6',
  // Edit-mode input — white bg, 13px dark text matching .inlineInput.
  editCellFontSize: '13px',
  editCellBackgroundColor: '#ffffff',
  editCellColor: '#212529'
};

// Map our LookupColumnType to a canvas-datagrid alignment hint. number cells
// right-align in the spirit of spreadsheets; text + datetime left-align.
const columnAlignment = (type: LookupColumnType): 'left' | 'right' => {
  return type === 'number' ? 'right' : 'left';
};

const LookupCanvasTable: React.FC<Props> = ({
  idPrefix,
  dictionary,
  onChange,
  onSave,
  dirty = false,
  isSaving = false,
  canManageWorkflow = true
}) => {
  const rootId = idPrefix || 'lookup-canvas-table';
  const { name, columns, rows, readonly } = dictionary;

  // Add-column popover state
  const [addColOpen, setAddColOpen] = useState(false);
  const [newColName, setNewColName] = useState('');
  const [newColType, setNewColType] = useState<LookupColumnType>('text');
  const [newColError, setNewColError] = useState<string | null>(null);

  // Column-deletion confirmation modal
  const [pendingDeleteKey, setPendingDeleteKey] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const pendingDeleteColumn = pendingDeleteKey
    ? columns.find(c => c.key === pendingDeleteKey)
    : null;

  // Latest dictionary in a ref so all canvas event handlers can be stable
  // useCallback identities — without this every cell edit would invalidate
  // them and force CanvasTable to recreate its schema / data subscriptions.
  const dictionaryRef = useRef(dictionary);
  dictionaryRef.current = dictionary;
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  // Last focused cell — updated on every click / selection so the paste
  // handler (below) knows where to write. `columnName` is looked up from the
  // current schema at the time of focus; a stale index would target the
  // wrong column after the user renames or deletes a column, but the ref is
  // rewritten fast enough (on every click) that it stays fresh in practice.
  const focusedCellRef = useRef<{ rowIndex: number; columnName: string } | null>(null);

  // Grid instance handle — set by CanvasTable's `onGridReady` callback,
  // cleared on unmount. Used to commit the active cell editor before Save
  // fires; without this, typing a value then hitting Cmd/Ctrl+Enter while
  // still inside the cell editor would send stale data (the edit hasn't
  // reached React state yet — canvas-datagrid only fires `endedit` on a
  // normal Enter / blur, which the user hasn't pressed).
  const gridRef = useRef<any | null>(null);
  const handleGridReady = useCallback((grid: any | null) => {
    gridRef.current = grid;
  }, []);

  // Blur any focused / selected cell the moment a save kicks off — covers
  // both the toolbar Save button (in `LookupTable.tsx`) and the
  // Cmd/Ctrl+Enter shortcut below. Watching the `isSaving` false→true
  // transition means we don't need to thread a callback through every
  // save path; the parent already flips the flag before the network call.
  // `selectNone(true)` also clears the active-cell overlay canvas-datagrid
  // draws around the last-focused cell — otherwise the user sees a stale
  // blue border on a cell they no longer own.
  const prevIsSavingRef = useRef(isSaving);
  useEffect(() => {
    if (!prevIsSavingRef.current && isSaving) {
      const grid = gridRef.current;
      try {
        grid?.selectNone?.(true);
      } catch (_) {
        /* older canvas-datagrid builds without `selectNone` — swallow */
      }
      focusedCellRef.current = null;
    }
    prevIsSavingRef.current = isSaving;
  }, [isSaving]);

  // ---- canvas-datagrid plumbing --------------------------------------------

  // Schema is: leading row-number column, user-defined columns, trailing
  // delete-row action column (omitted in readonly mode). Memoized so
  // CanvasTable's schema-sync effect only fires when the column set actually
  // changes.
  const schema = useMemo<CanvasTableSchemaColumn[]>(() => {
    // Synthetic row-number column removed — canvas-datagrid's built-in
    // auto-numbered row-header column is the visible index column instead.
    const cols: CanvasTableSchemaColumn[] = [];
    columns.forEach(c => {
      cols.push({
        name: c.key,
        title: c.label,
        type: 'string',
        // Matches `.colHeader { width: 240px }` from the reference CSS.
        width: 240,
        // canvas-datagrid honors per-column horizontalAlignment when set.
        // Right-align number columns to match spreadsheet convention.
        horizontalAlignment: columnAlignment(c.type)
      } as CanvasTableSchemaColumn);
    });
    if (!readonly) {
      cols.push({
        name: DELETE_COL,

        title: '',
        type: 'string',
        width: 50,
        resizable: false,
        allowResize: false,
        horizontalAlignment: 'center'
      } as unknown as CanvasTableSchemaColumn);
    }
    return cols;
  }, [columns, readonly]);

  // canvas-datagrid mutates row objects in-place on commit. Build a fresh
  // row-shaped array for each render that aligns with the current schema.
  // The trailing `__delete` field is just a paint hint — its value is the
  // glyph the renderer draws.
  const data = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const matched = q
      ? rows.filter(r =>
          Object.values(r.values).some(v =>
            String(v ?? '')
              .toLowerCase()
              .includes(q)
          )
        )
      : rows;
    return matched.map(r => {
      const base: Record<string, unknown> = { ...r.values };
      // Delete column has no text — the trash icon is painted by hand below.
      // We still set the key so canvas-datagrid keeps the column in the row.
      if (!readonly) base[DELETE_COL] = '';
      return base;
    });
  }, [rows, readonly, searchQuery]);

  // Only the trash column gets click-as-action treatment; it also blocks
  // canvas-datagrid from entering edit mode on that cell.
  const actionColumns = useMemo(() => (readonly ? [] : [DELETE_COL]), [readonly]);

  // ---- mutating actions ----------------------------------------------------

  const addRow = useCallback(() => {
    const dict = dictionaryRef.current;
    if (dict.readonly) return;
    const newId = generateRowId();
    const empty: LookupRow = {
      id: newId,
      values: dict.columns.reduce<Record<string, string>>((acc, c) => {
        acc[c.key] = '';
        return acc;
      }, {})
    };
    onChange({ ...dict, rows: [...dict.rows, empty] });
  }, [onChange]);

  const deleteRow = useCallback(
    (rowIndex: number) => {
      const dict = dictionaryRef.current;
      if (dict.readonly) return;
      const row = dict.rows[rowIndex];
      if (!row) return;
      onChange({ ...dict, rows: dict.rows.filter(r => r.id !== row.id) });
    },
    [onChange]
  );

  // canvas-datagrid has already written `value` into the in-memory data
  // object — we still want React's source of truth to mirror it so the
  // dirty flag flips and a subsequent save sends the right payload.
  const handleEditEnd = useCallback(
    (e: CanvasTableEditEvent) => {
      const dict = dictionaryRef.current;
      if (dict.readonly) return;
      const row = dict.rows[e.rowIndex];
      if (!row) return;
      const newValues = { ...row.values, [e.columnName]: String(e.value ?? '') };
      const nextRows = dict.rows.map((r, i) =>
        i === e.rowIndex ? { ...r, values: newValues } : r
      );
      onChange({ ...dict, rows: nextRows });
    },
    [onChange]
  );

  // Paint custom backgrounds + glyphs for the structural columns. canvas-
  // datagrid identifies cell type via `cell.style` — `'rowHeaderCell'` is the
  // leftmost auto-numbered column, `'cornerCell'` is its intersection with
  // the column-header row.
  //   * row-number column — gray-0 bg + centred digit (canvas-datagrid's own
  //     `rowHeaderCellHorizontalAlignment: 'center'` is silently ignored).
  //   * corner cell — gray-1 bg matching the rest of the column-header row.
  //   * delete-action column — white bg + vector trash icon.
  // We always `preventDefault()` so canvas-datagrid's default paint never
  // runs over our custom one.
  const handleRenderCell = useCallback((event: any) => {
    try {
      const cell = event.cell;
      const ctx = event.ctx;
      if (!cell || !ctx) return;

      // Only the synthetic `__delete` column is painted by hand here. The
      // row-number column is canvas-datagrid's built-in row-header.
      const colName = cell.header?.name;
      const isDeleteCell = colName === DELETE_COL && cell.rowIndex >= 0;
      const isDeleteHeader = colName === DELETE_COL && (cell.rowIndex == null || cell.rowIndex < 0);

      // canvas-datagrid's built-in row-header column. Detection is broad
      // because the property used varies across builds. We repaint these
      // ourselves so every row — active *and* non-active — gets the same
      // centred light-grey digit; canvas-datagrid's own paint path applies
      // alignment only to the active-row variant.
      const style = cell.style;
      const isRowHeader =
        style === 'rowHeaderCell' ||
        cell.isRowHeader === true ||
        cell.rowHeaderCell === true ||
        cell.header?.style === 'rowHeaderCell' ||
        ((cell.columnIndex == null || cell.columnIndex === -1) &&
          cell.rowIndex != null &&
          cell.rowIndex >= 0);

      if (!isDeleteCell && !isDeleteHeader && !isRowHeader) return;

      const { x, y, width: w, height: h } = cell;

      // ctx state is global. Scope every mutation to this block with
      // save/restore so `strokeStyle`/`fillStyle` don't bleed into subsequent
      // user-column paints.
      ctx.save();
      try {
        // Background — row-header column gets gray-0; delete-column header
        // gets the column-header grey; delete data cells stay white.
        if (isRowHeader) {
          ctx.fillStyle = '#fafbfc';
        } else if (isDeleteHeader) {
          ctx.fillStyle = '#f1f3f5';
        } else {
          ctx.fillStyle = '#ffffff';
        }
        ctx.fillRect(x, y, w, h);

        // Right + bottom hairlines (1 px gray-1) matching `.dataCell { border:
        // 1px solid gray-1 }`. Offset by 0.5 so the 1 px stroke lands on a
        // pixel boundary instead of straddling two.
        ctx.strokeStyle = '#f1f3f5';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x + w - 0.5, y);
        ctx.lineTo(x + w - 0.5, y + h);
        ctx.moveTo(x, y + h - 0.5);
        ctx.lineTo(x + w, y + h - 0.5);
        ctx.stroke();

        if (isRowHeader) {
          ctx.font = '500 11px sans-serif';
          ctx.fillStyle = '#adb5bd';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(String(cell.value ?? ''), x + w / 2, y + h / 2);
        } else if (isDeleteCell) {
          ctx.strokeStyle = '#adb5bd';
          drawTrashIcon(ctx, x + w / 2, y + h / 2);
        }
        // Delete-column header — bg + borders only.
      } finally {
        ctx.restore();
      }

      event.preventDefault();
    } catch (_) {
      /* swallow */
    }
  }, []);

  // Text-pass mutations:
  //   * Column-header titles — bold 12px so they read heavier than data.
  //   * Row-header digits — fallback paint for canvas-datagrid versions that
  //     skip the `rendercell` event for the auto-numbered column and fall
  //     straight through to `rendertext`. Same paint as the rendercell branch
  //     above so whichever event fires, the column ends up centred.
  // Column-header text — bold 12px. The auto-numbered row header is gone
  // (we disabled it on the CanvasTable side) so there's nothing else for
  // this hook to do; the synthetic `__rownum` column is painted from
  // `handleRenderCell`.
  const handleRenderText = useCallback((event: any) => {
    try {
      const cell = event.cell;
      const ctx = event.ctx;
      if (!cell || !ctx) return;

      const isColumnHeader =
        cell.style === 'columnHeaderCell' ||
        cell.isColumnHeader === true ||
        cell.isHeader === true ||
        cell.header?.style === 'columnHeaderCell';

      // canvas-datagrid keeps falling back to `name` ("__delete") in this
      // build regardless of the schema's `title` value. Killing the text
      // pass for that one header cell is the most reliable way to keep the
      // column visually empty.
      if (isColumnHeader && cell.header?.name === DELETE_COL) {
        event.preventDefault();
        return;
      }

      if (isColumnHeader) {
        // Suppress canvas-datagrid's default text paint — `handleAfterRenderCell`
        // is the single source of truth for the column-header title + type
        // icon. Painting here too caused a duplicated title (one at left,
        // one shifted right) because both landed on the canvas in sequence.
        event.preventDefault();
        return;
      }

      // Row-header column — repaint the centred digit in the text pass too.
      // canvas-datagrid's own row-header text renderer applies alignment
      // only to the active-row variant; for non-active rows it falls back
      // to left-aligned. Painting + `preventDefault()` here locks in
      // centring for every row.
      const style = cell.style;
      const isRowHeader =
        style === 'rowHeaderCell' ||
        cell.isRowHeader === true ||
        cell.rowHeaderCell === true ||
        cell.header?.style === 'rowHeaderCell' ||
        ((cell.columnIndex == null || cell.columnIndex === -1) &&
          cell.rowIndex != null &&
          cell.rowIndex >= 0);
      if (isRowHeader) {
        ctx.save();
        try {
          ctx.font = '500 11px sans-serif';
          ctx.fillStyle = '#adb5bd';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(String(cell.value ?? ''), cell.x + cell.width / 2, cell.y + cell.height / 2);
        } finally {
          ctx.restore();
        }
        event.preventDefault();
        return;
      }

      const colName = cell.header?.name;
      // Trash icon — repaint here in the text pass too. canvas-datagrid
      // clears the cell text area between `rendercell` and `rendertext`, so
      // the icon drawn from `handleRenderCell` was getting wiped before the
      // user ever saw it. Paint again + preventDefault to lock it in.
      if (colName === DELETE_COL && cell.rowIndex != null && cell.rowIndex >= 0) {
        ctx.save();
        try {
          ctx.strokeStyle = '#adb5bd';
          drawTrashIcon(ctx, cell.x + cell.width / 2, cell.y + cell.height / 2);
        } finally {
          ctx.restore();
        }
        event.preventDefault();
        return;
      }
      if (colName === DELETE_COL) {
        // Synthetic-column header — no glyph, just the gray-1 bg painted
        // from `handleRenderCell`.
        event.preventDefault();
        return;
      }
    } catch (_) {
      /* swallow */
    }
  }, []);

  // `afterrendercell` fires AFTER canvas-datagrid's default cell + text paint.
  // This is the only event whose paint lands on top of the default text path
  // for the row-header column — `rendercell` / `rendertext` `preventDefault()`
  // is ignored for that column in this build, so the default left-aligned
  // digit keeps overwriting our centred one. Painting *here* finally wins.
  const handleAfterRenderCell = useCallback((event: any) => {
    try {
      const cell = event.cell;
      const ctx = event.ctx;
      if (!cell || !ctx) return;

      const style = cell.style;
      // Row-header check has to come first because `cell.isHeader === true`
      // can match BOTH column- and row-header cells in this canvas-datagrid
      // build. Without the exclusion below, the type icon would paint on
      // every row-number cell as well.
      const isRowHeader =
        style === 'rowHeaderCell' ||
        cell.isRowHeader === true ||
        cell.rowHeaderCell === true ||
        cell.header?.style === 'rowHeaderCell' ||
        ((cell.columnIndex == null || cell.columnIndex === -1) &&
          cell.rowIndex != null &&
          cell.rowIndex >= 0);
      const isColumnHeader =
        !isRowHeader &&
        (style === 'columnHeaderCell' ||
          cell.isColumnHeader === true ||
          cell.isHeader === true ||
          cell.header?.style === 'columnHeaderCell');
      if (isColumnHeader) {
        // Skip the trailing `__delete` column header — its title stays blank.
        const colName = cell.header?.name;
        const labelStr = String(cell.value ?? '');
        if (colName === DELETE_COL || labelStr === '__delete') return;

        // Find the matching schema column to derive the type. Try by name
        // first; fall back to label match if canvas-datagrid didn't populate
        // `cell.header.name` for column-header cells in this build.
        const dict = dictionaryRef.current;
        const userColumn =
          dict?.columns?.find(c => c.key === colName) ??
          dict?.columns?.find(c => c.label === labelStr);
        // Phantom column headers (canvas-datagrid sometimes extends past
        // the schema) — skip so we don't paint a default 'text' icon
        // beyond the table's last real column.
        if (!userColumn) return;
        const colType: LookupColumnType = userColumn.type;

        ctx.save();
        try {
          // Repaint bg + bottom border so the column-header strip keeps its
          // rule. canvas-datagrid's default paint runs at 12 px; we land the
          // 14 px bold title + 16 px PNG icon on top of it.
          ctx.fillStyle = '#f1f3f5';
          ctx.fillRect(cell.x, cell.y, cell.width, cell.height);
          ctx.strokeStyle = '#dee2e6';
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(cell.x, cell.y + cell.height - 0.5);
          ctx.lineTo(cell.x + cell.width, cell.y + cell.height - 0.5);
          ctx.moveTo(cell.x + cell.width - 0.5, cell.y);
          ctx.lineTo(cell.x + cell.width - 0.5, cell.y + cell.height);
          ctx.stroke();

          // Type icon — same PNG assets the transformation table uses.
          const icon = getLookupTypeIcon(colType);
          const iconSize = 16;
          const iconX = cell.x + 6;
          const iconY = cell.y + (cell.height - iconSize) / 2;
          if (icon && icon.width !== 0) {
            ctx.drawImage(icon, iconX, iconY, iconSize, iconSize);
          }

          // Title — to the right of the icon (icon + 8 px gap).
          ctx.font = 'bold 14px sans-serif';
          ctx.fillStyle = '#212529';
          ctx.textAlign = 'left';
          ctx.textBaseline = 'middle';
          ctx.fillText(labelStr, iconX + iconSize + 8, cell.y + cell.height / 2);
        } finally {
          ctx.restore();
        }
        return;
      }

      // Row-header (index) column — canvas-datagrid's own digit text is
      // hidden via the CanvasTable text-hide effect (color set to transparent),
      // so this is the only paint that reaches the row-number cells. Centred
      // gray-5 digit; we re-paint the bg + hairlines first so the cell
      // matches the rest of the column rhythm.
      if (isRowHeader) {
        ctx.save();
        try {
          ctx.fillStyle = '#fafbfc';
          ctx.fillRect(cell.x, cell.y, cell.width, cell.height);
          ctx.strokeStyle = '#f1f3f5';
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(cell.x + cell.width - 0.5, cell.y);
          ctx.lineTo(cell.x + cell.width - 0.5, cell.y + cell.height);
          ctx.moveTo(cell.x, cell.y + cell.height - 0.5);
          ctx.lineTo(cell.x + cell.width, cell.y + cell.height - 0.5);
          ctx.stroke();
          ctx.font = '500 11px sans-serif';
          ctx.fillStyle = '#adb5bd';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(String(cell.value ?? ''), cell.x + cell.width / 2, cell.y + cell.height / 2);
        } finally {
          ctx.restore();
        }
      }
    } catch (_) {
      /* swallow */
    }
  }, []);

  const handleActionClick = useCallback(
    (e: CanvasTableActionEvent) => {
      if (e.columnName === DELETE_COL) deleteRow(e.rowIndex);
    },
    [deleteRow]
  );

  const resetAddCol = () => {
    setNewColName('');
    setNewColType('text');
    setNewColError(null);
  };

  const submitAddColumn = () => {
    const dict = dictionaryRef.current;
    const label = newColName.trim();
    if (!label) {
      setNewColError('Column name is required');
      return;
    }
    const existing = new Set(dict.columns.map(c => c.key.toLowerCase()));
    if (existing.has(label.toLowerCase())) {
      setNewColError('Column already exists');
      return;
    }
    const newCol: LookupColumn = { key: label, label, type: newColType };
    const nextRows = dict.rows.map(r => ({ ...r, values: { ...r.values, [label]: '' } }));
    onChange({ ...dict, columns: [...dict.columns, newCol], rows: nextRows });
    resetAddCol();
    setAddColOpen(false);
  };

  const confirmDeleteColumn = () => {
    if (!pendingDeleteKey) return;
    const dict = dictionaryRef.current;
    const nextCols = dict.columns.filter(c => c.key !== pendingDeleteKey);
    const nextRows = dict.rows.map(r => {
      const { [pendingDeleteKey]: _drop, ...rest } = r.values;
      return { ...r, values: rest };
    });
    onChange({ ...dict, columns: nextCols, rows: nextRows });
    setPendingDeleteKey(null);
  };

  const handleExport = () => {
    const safeName = name.replace(/[^a-z0-9-_ ]/gi, '_');
    downloadCsv(`${safeName}.csv`, buildCsv(dictionary));
  };

  // ---- keyboard shortcuts on the canvas container --------------------------
  // canvas-datagrid emits its own keydown events on the wrapper div, so we
  // listen at the container level. Cmd/Ctrl+Enter saves; Shift+Enter adds
  // a new row (while NOT in edit mode — once in edit mode canvas-datagrid
  // owns Enter for commit).
  //
  // Paste (Cmd/Ctrl+V) is handled by canvas-datagrid natively and surfaced
  // to React via `CanvasTable`'s `onAfterPaste` prop below — the DOM paste
  // event never bubbles up to this container because canvas-datagrid's
  // clipboard input intercepts it out-of-tree.
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const node = containerRef.current;
    if (!node || readonly) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      if (e.shiftKey) {
        e.preventDefault();
        addRow();
      } else if (e.metaKey || e.ctrlKey) {
        e.preventDefault();
        // Commit the active cell editor first if the user is still inside
        // it. canvas-datagrid only fires `endedit` (which our handler
        // relies on to mirror the value into React state) on a plain
        // Enter / blur; Cmd/Ctrl+Enter reaches us with the typed value
        // still trapped in the editor input. Calling `endEdit()` here
        // triggers `endedit` synchronously, which flushes through
        // `handleEditEnd` → `onChange` → parent's `setDraft`. Safe no-op
        // when no editor is open.
        let hadOpenEditor = false;
        try {
          hadOpenEditor = !!gridRef.current?.input?.editCell;
          gridRef.current?.endEdit?.(false);
        } catch (_) {
          /* swallow — some canvas-datagrid builds throw on endEdit with
             no active editor */
        }
        // React batches the `setDraft` from the endEdit above, so the
        // closure inside `onSaveRef.current` still captures the *pre*-edit
        // draft at this point, and `dirtyRef.current` still reads the
        // pre-edit value. Defer to the next macrotask so React has a
        // chance to re-render and refresh both refs before we save.
        const fireSave = () => {
          if (onSaveRef.current && dirtyRef.current) onSaveRef.current();
        };
        if (hadOpenEditor) {
          setTimeout(fireSave, 0);
        } else {
          fireSave();
        }
      }
    };
    node.addEventListener('keydown', onKey);
    return () => node.removeEventListener('keydown', onKey);
  }, [readonly, addRow]);

  // Any mousedown OUTSIDE the grid should commit the active cell editor
  // synchronously before the click handler on the target runs. Handles the
  // "type value → click Save button (without pressing Enter)" case: the
  // button's blur alone doesn't reliably reach canvas-datagrid's editor
  // (its blur handler wires to a hidden control input, not the visible
  // editor), so `endedit` never fires and the pending value stays trapped
  // in the editor input until the user manually presses Enter. Capture-
  // phase so we run BEFORE the button's click; `endEdit(false)` commits
  // synchronously → `endedit` fires → `handleEditEnd` → `onChange` → parent
  // sets the new draft (batched). The Save handler already awaits one tick
  // so the batched setState flushes before it reads `draftRef.current`.
  useEffect(() => {
    if (readonly) return;
    const container = containerRef.current;
    if (!container) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (!(e.target instanceof Node)) return;
      if (container.contains(e.target)) return;
      const grid = gridRef.current;
      if (!grid?.input?.editCell) return;
      try {
        grid.endEdit?.(false);
      } catch (_) {
        /* swallow — some canvas-datagrid builds throw when no editor */
      }
    };
    document.addEventListener('mousedown', onDocMouseDown, true);
    return () => document.removeEventListener('mousedown', onDocMouseDown, true);
  }, [readonly]);

  // canvas-datagrid handles Cmd/Ctrl+V (paste), Delete/Backspace (clear
  // cell), and Cmd/Ctrl+X (cut) natively — it mutates its own in-memory
  // data but never fires `endedit`, so `handleEditEnd` above doesn't run
  // and the dirty flag stays false. This callback fires from CanvasTable's
  // `onAfterExternalEdit` (which listens for `afterpaste`, `afterdelete`,
  // `aftercut`) with one entry per mutated cell; we replay ALL of them
  // through a single `onChange` so React only sees one state update.
  // Calling `handleEditEnd` per cell in a loop fires `onChange` N times,
  // which forces canvas-datagrid to re-sync its `data` prop N times —
  // each re-sync resets the grid's scroll position.
  const handleAfterExternalEdit = useCallback(
    (cells: CanvasTableEditEvent[]) => {
      const dict = dictionaryRef.current;
      if (dict.readonly || cells.length === 0) return;
      // Group patches by row so a paste hitting multiple columns of the
      // same row folds into one `{ ...row.values, ...patches }` merge.
      const patchesByRow = new Map<number, Record<string, string>>();
      for (const c of cells) {
        const patch = patchesByRow.get(c.rowIndex) ?? {};
        patch[c.columnName] = String(c.value ?? '');
        patchesByRow.set(c.rowIndex, patch);
      }
      const nextRows = dict.rows.map((r, i) => {
        const patch = patchesByRow.get(i);
        if (!patch) return r;
        return { ...r, values: { ...r.values, ...patch } };
      });
      onChange({ ...dict, rows: nextRows });
    },
    [onChange]
  );

  // ---- render --------------------------------------------------------------

  return (
    <Main id={`${rootId}-main`}>
      <MainHeader id={`${rootId}-header`}>
        <span id={`${rootId}-title`}>Lookup: {name}</span>
        <div id={`${rootId}-spacer`} style={{ flex: 1 }} />
        <MainActions id={`${rootId}-actions`}>
          {canManageWorkflow ? (
            readonly ? (
              <PermissionGuard allowed={canManageWorkflow}>
                <ToolBtn id={`${rootId}-add-row`} type="button" onClick={addRow} disabled>
                  <TbPlus size={12} /> Add Row
                </ToolBtn>
              </PermissionGuard>
            ) : (
              <Tooltip label="Shift + Enter" position="top" withArrow>
                <ToolBtn id={`${rootId}-add-row`} type="button" onClick={addRow}>
                  <TbPlus size={12} /> Add Row
                </ToolBtn>
              </Tooltip>
            )
          ) : (
            <PermissionGuard allowed={canManageWorkflow}>
              <ToolBtn id={`${rootId}-add-row`} type="button" onClick={addRow} disabled={readonly}>
                <TbPlus size={12} /> Add Row
              </ToolBtn>
            </PermissionGuard>
          )}
          {!readonly && (
            <Popover
              id={`${rootId}-add-column-popover`}
              opened={addColOpen}
              onChange={opened => {
                setAddColOpen(opened);
                if (!opened) resetAddCol();
              }}
              position="bottom-end"
              withArrow
              shadow="md"
              width={260}
              trapFocus
            >
              <Popover.Target>
                <ToolBtn
                  id={`${rootId}-add-column`}
                  type="button"
                  onClick={() => setAddColOpen(o => !o)}
                >
                  <TbColumnInsertRight size={12} /> Add Column
                </ToolBtn>
              </Popover.Target>
              <Popover.Dropdown>
                <TextInput
                  id={`${rootId}-add-column-name`}
                  label="Column name"
                  placeholder="e.g. region_code"
                  value={newColName}
                  onChange={e => {
                    setNewColName(e.currentTarget.value);
                    if (newColError) setNewColError(null);
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      submitAddColumn();
                    } else if (e.key === 'Escape') {
                      setAddColOpen(false);
                      resetAddCol();
                    }
                  }}
                  error={newColError ?? undefined}
                  autoFocus
                  size="xs"
                />
                <Select
                  id={`${rootId}-add-column-type`}
                  label="Type"
                  data={[
                    { value: 'text', label: 'Text' },
                    { value: 'number', label: 'Number' },
                    { value: 'datetime', label: 'Date / Time' }
                  ]}
                  value={newColType}
                  onChange={v => setNewColType((v as LookupColumnType) ?? 'text')}
                  mt={8}
                  size="xs"
                  allowDeselect={false}
                  withinPortal={false}
                  comboboxProps={{ withinPortal: false }}
                />
                <Button
                  id={`${rootId}-create-column`}
                  fullWidth
                  size="xs"
                  mt={12}
                  onClick={submitAddColumn}
                  leftSection={<TbPlus size={12} />}
                  aria-label="Create column"
                >
                  Create column
                </Button>
              </Popover.Dropdown>
            </Popover>
          )}
          {!readonly && columns.length > 0 && (
            <Select
              id={`${rootId}-delete-column-select`}
              size="xs"
              placeholder="Delete column..."
              data={columns.map(c => ({ value: c.key, label: c.label }))}
              value={null}
              onChange={v => v && setPendingDeleteKey(v)}
              clearable={false}
              allowDeselect={false}
              w={160}
              comboboxProps={{ withinPortal: true }}
              // Match the 26 px ToolBtn height so the dropdown lines up
              // visually with Add Row / Add Column / Export / Save.
              styles={{
                input: { height: 26, minHeight: 26, fontSize: 12, paddingTop: 0, paddingBottom: 0 },
                section: { width: 22 }
              }}
            />
          )}
          <TextInput
            id={`${rootId}-search-input`}
            size="xs"
            placeholder="Search..."
            leftSection={<TbSearch size={12} />}
            value={searchQuery}
            onChange={e => setSearchQuery(e.currentTarget.value)}
            w={160}
            styles={{
              input: { height: 26, minHeight: 26, fontSize: 12, paddingTop: 0, paddingBottom: 0 },
              section: { width: 22 }
            }}
          />
          <ToolBtn id={`${rootId}-export`} type="button" onClick={handleExport}>
            <TbDownload size={12} /> Export
          </ToolBtn>
          {onSave && !readonly && (
            <Tooltip label={`${SAVE_KEY_LABEL} + Enter`} position="top" withArrow disabled={!dirty}>
              <ToolBtn
                id={`${rootId}-save`}
                type="button"
                $primary
                onClick={onSave}
                disabled={!dirty || isSaving}
                aria-label="Save changes"
              >
                <TbCheck size={12} /> {isSaving ? 'Saving…' : 'Save'}
              </ToolBtn>
            </Tooltip>
          )}
        </MainActions>
      </MainHeader>

      <div
        id={`${rootId}-table-host`}
        ref={containerRef}
        // Fill the remaining vertical space inside <Main>. CanvasTable sets
        // `height: 100%` on its inner wrapper, so we need a definite height
        // here for canvas-datagrid to lay out its viewport correctly.
        style={{
          flex: 1,
          minHeight: 240,
          position: 'relative',
          outline: 'none'
        }}
        tabIndex={0}
      >
        {rows.length === 0 ? (
          <div
            id={`${rootId}-empty-state`}
            style={{
              color: '#868e96',
              fontStyle: 'italic',
              padding: '16px 12px'
            }}
          >
            No rows yet.
          </div>
        ) : (
          <CanvasTable
            data={data}
            schema={schema}
            editable={!readonly}
            onEditEnd={handleEditEnd}
            onActionCellClick={handleActionClick}
            actionColumns={actionColumns}
            gridStyleOverrides={GRID_THEME}
            onRenderCell={handleRenderCell}
            onRenderText={handleRenderText}
            onAfterRenderCell={handleAfterRenderCell}
            onAfterExternalEdit={handleAfterExternalEdit}
            onGridReady={handleGridReady}
            lockedColumnWidths={readonly ? undefined : { [DELETE_COL]: 50 }}
            zoomLevel={1}
            rowPinning={{ top: [] }}
            columnPinning={{ left: [] }}
            scrolledPosition={{ top: 0, left: 0 }}
            onFocusedCell={cell => {
              // CanvasTable emits 1-indexed row/column via
              // `rowIndex + 1` / `columnIndex + 1`. Reverse that and look
              // up the column name from the current schema so the paste
              // handler can drive `handleEditEnd` directly.
              const rowIndex = Number(cell.rowId) - 1;
              const columnIndex = Number(cell.columnId) - 1;
              const columnName = schema[columnIndex]?.name;
              // Skip header rows (rowIndex < 0), the trailing delete
              // action column, and any focus event where the column
              // couldn't be resolved.
              if (columnName == null || columnName === DELETE_COL || rowIndex < 0) {
                focusedCellRef.current = null;
                return;
              }
              focusedCellRef.current = { rowIndex, columnName };
            }}
            setSelectedCells={() => {}}
            handleSelection={cells => {
              // Fires on click for single-cell selection — mirror the
              // same book-keeping as `onFocusedCell` so a fresh click
              // always updates the paste target even if the two callbacks
              // ever drift.
              if (cells.length !== 1) return;
              const [cell] = cells;
              const rowIndex = Number(cell.rowId) - 1;
              const columnIndex = Number(cell.columnId) - 1;
              const columnName = schema[columnIndex]?.name;
              if (columnName == null || columnName === DELETE_COL || rowIndex < 0) {
                focusedCellRef.current = null;
                return;
              }
              focusedCellRef.current = { rowIndex, columnName };
            }}
          />
        )}
      </div>

      {!readonly && (
        <AddRowFooter id={`${rootId}-footer-add-row`} type="button" onClick={addRow}>
          <TbPlus size={12} color="#228be6" />
          Click to add new mapping rule
        </AddRowFooter>
      )}

      <DeleteConfirmModal
        id={`${rootId}-delete-column-modal`}
        isOpen={!!pendingDeleteColumn}
        onClose={() => setPendingDeleteKey(null)}
        onConfirm={confirmDeleteColumn}
        isLoading={false}
        title="Delete column"
        description={
          pendingDeleteColumn
            ? `Delete column "${pendingDeleteColumn.label}" and all its values from this dictionary? This will affect every row.`
            : ''
        }
        submitLabel="Delete"
        cancelLabel="Cancel"
      />
    </Main>
  );
};

export default LookupCanvasTable;
